import * as React from 'react';
import { useRef } from 'react';

import { PictureProfile } from '../../../ResizedImage/PictureProfile.csharp';
import { DiscountType } from '../../Blocks/DiscountType.csharp';
import { DiscountVariant } from '../../Blocks/DiscountVariant.csharp';
import { getExperimentAwareUrl, resolveExperiments } from '../../Experimentation/Components/ResolveExperimentId';
import { ExperimentationEventProductClickDetails } from '../../Experimentation/Models/ExperimentationEventProductClickDetails.csharp';
import { SearchResultProductViewModel } from '../../Filtering/Api/SearchResultProductViewModel.csharp';
import { trackEpiFindClick } from '../../Filtering/Api/TrackEpiFindClick';
import { ProductSortingType } from '../../Filtering/Sorting/ProductSortingType.csharp';
import { post } from '../../ReactComponents/API';
import { ProductTopLabel } from '../../ReactComponents/InfoLabel/ProductTopLabel';
import { PriceDisplay } from '../../ReactComponents/PriceDisplay';
import { ResponsivePicture } from '../../ReactComponents/ResponsivePicture/ResponsivePicture';
import { GoogleTagManager } from '../../Tracking/GoogleTagManager/GoogleTagManager';
import { handleClickEvents } from '../../Tracking/GoogleTagManager/HandleClickEvents';

interface SearchItemProps {
    item: SearchResultProductViewModel;
    pictureProfile: PictureProfile;
    sortingType: ProductSortingType;
    epiFindClickTrackingUrl: string;
    experimentProductClickUrl: string;
}

export const SearchItem = ({ item, pictureProfile, epiFindClickTrackingUrl, experimentProductClickUrl, sortingType }: SearchItemProps) => {
    const [itemLink, setItemLink] = React.useState(item.link);
    const [experiments, setExperiments] = React.useState<string[]>([]);

    const searchItemRef = useRef(null);

    const hasPercentagePromotions = item.promotions && item.promotions.some((promotion) => promotion.promotionInfo.type == DiscountType.Product);

    React.useEffect(() => {
        const resolvedExperiments = resolveExperiments(searchItemRef.current);

        setExperiments(resolvedExperiments);
        setItemLink(getExperimentAwareUrl(item.link, resolvedExperiments));

        GoogleTagManager.searchProductImpression(item);
    }, []);

    const track = () => {
        trackClickHit();
        trackExperimentProductClick();
    };

    const trackClickHit = () => trackEpiFindClick(epiFindClickTrackingUrl, item.position, item.optimizelySearchTrackingInfo);

    const trackExperimentProductClick = () => {
        if (experiments.length > 0) {
            const url = getExperimentAwareUrl(experimentProductClickUrl, experiments);
            const promotions = item.promotions.map((promotion) => promotion.mainInfo);

            const eventDetails: ExperimentationEventProductClickDetails = {
                position: item.position,
                sku: item.code,
                promotionInfo: promotions.join(),
                sortingType: ProductSortingType[sortingType ?? -1]
            };

            post(url, eventDetails);
        }
    };

    return (
        <li ref={searchItemRef} key={item.nameInfo.additionalName} onClick={track}>
            <a href={itemLink} {...handleClickEvents((e) => GoogleTagManager.searchItemClick(e, item))}>
                <div className="RichSearch__ItemInner">
                    <div className="RichSearch__Image">
                        {item.image && <ResponsivePicture model={item.image.picture} profile={pictureProfile} isLazyLoaded />}
                        <ProductTopLabel
                            promotions={item.promotions}
                            showFixedLowPriceBadge={false}
                            showNewProductLabel={false}
                            variant={DiscountVariant.Small}
                            showPromoStar={true}
                            showGoodPurchaseBadge={false}
                            showOnlyFreeItemBadge={true}
                            badgeSettings={item.badgeSettings}
                        />
                    </div>
                    <div className="RichSearch__ItemContent">
                        <div className="RichSearch__ItemHeader">
                            {item.nameInfo.mainName && <strong>{item.nameInfo.mainName}</strong>}
                            <span>{item.nameInfo.additionalName}</span>
                        </div>
                        <div className="RichSearch__Price">
                            {hasPercentagePromotions ? (
                                <>
                                    <span className="ProductCard__OriginalPrice">
                                        <PriceDisplay roundUp price={item.placedPrice} />
                                    </span>
                                    {item.discountedPrice && (
                                        <span className="ProductCard__CurrentPrice ProductCard__CurrentPrice--Discounted">
                                            <PriceDisplay price={item.discountedPrice} roundUp />
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="ProductCard__CurrentPrice">
                                    <PriceDisplay roundUp price={item.placedPrice} />
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </a>
        </li>
    );
};
